import { all, call, fork, put, takeEvery, takeLatest, select } from "redux-saga/effects";
import { INICIA_ESCOLA, SALVA_ESCOLA, ATUALIZA_ESCOLA, BUSCA_ESCOLA_EDICAO, REMOVE_ESCOLA, LISTA_ESCOLA, LISTA_ANOS_LETIVOS_POR_ESCOLA } from "constants/ActionTypes";
import requisicaoApi from 'util/Api';
import { userSignOut } from "actions/Auth";
import { showAuthMessage, exibirMensagemErro, salvaEscolaSucesso, removeEscolaSucesso, inicioEscolaSucesso, buscaEscolaEdicaoSucesso, listaEscolaSucesso, listaCursosEscolaSucesso, listaAnosLetivosPorEscolaSucesso } from "actions/Escola";

/*#################################################################################################################*/
/* --------------------------------------------- Requests do tipo GET ---------------------------------------------*/         

const getEscolas = async () =>
  await requisicaoApi.get('/escolas')
    .then(resposta => resposta)
    .catch(error => error);

const getLocaisControlados = async (isEscola) =>
  await requisicaoApi.get('/locais?IsEscola='+isEscola)
    .then(resposta => resposta)
    .catch(error => error);

// Busca um Local Controlado específico por id (usado para garantir exibição correta no modo de edição)
const getLocalControladoPorId = async (id) =>
  await requisicaoApi.get('/locais/' + id)
    .then(resposta => resposta)
    .catch(error => error);

const getEscolaEdicao = async (id) =>
  await requisicaoApi.get('/escolas/' + id)
    .then(resposta => resposta)
    .catch(error => error);

const getListaEscola = async (nomeFiltro) => 
  await requisicaoApi.get('/escolas?Nome=' +  (nomeFiltro || ''))
    .then(resposta => resposta)
    .catch(error => error);

// Cursos auxiliares para combo e vínculos
const getCursos = async () =>
  await requisicaoApi.get('/cursos')
    .then(resposta => resposta)
    .catch(error => error);

// Cursos vinculados a uma escola específica
const getCursosDaEscola = async (idEscola) =>
  await requisicaoApi.get(`/escolas/${idEscola}/cursos`)
    .then(resposta => resposta)
    .catch(error => error);

const getAnosLetivosPorEscola = async (escolaId) =>
  await requisicaoApi.get(`/anos-letivos?EscolaId=${escolaId}`)
    .then(resposta => resposta)
    .catch(error => error);

/*#################################################################################################################*/
/* ------------------------------------- Requests do tipo POST, PUT E DELETE --------------------------------------*/     

const adicionaEscolaRequest = async (escola) => await requisicaoApi.post('/escolas', escola)
  .then(resposta => resposta)
  .catch(error => error);

const atualizaEscolaRequest = async (escola) => await requisicaoApi.put('/escolas/' + (escola.id || escola.Id || ''), escola)
  .then(resposta => resposta)
  .catch(error => error);

const deleteEscolaRequest = async (id) =>
  await requisicaoApi.delete('/escolas/' + id)
  .then(resposta => resposta)
  .catch(error => error);

/*#################################################################################################################*/
/* ------------------------------------------ Funções das Sagas ---------------------------------------------------*/

function* fnIniciaEscola() {
  try {
    const [retEscolas, retLocais, retCursos] = yield all([
      call(getEscolas),
      // Na inicialização padrão, carregamos apenas Locais Controlados que AINDA NÃO são escolas (IsEscola=false)
      // para viabilizar o fluxo de criação com a lista correta de opções disponíveis.
      call(getLocaisControlados, false),
      call(getCursos)
    ]);

    if (retEscolas.message) {
      yield put(showAuthMessage(JSON.stringify(retEscolas.message)));
      return;
    }

    if (retLocais.message) {
      // Não bloqueia a página; apenas não disponibiliza a lista de locais
      console.warn('[EscolaSaga] Falha ao obter locais controlados:', retLocais.message);
      yield put(inicioEscolaSucesso(retEscolas.data, []));
    } else {
      yield put(inicioEscolaSucesso(retEscolas.data, retLocais.data));
    }

    if (retCursos && !retCursos.message) {
      // Normaliza possíveis formatos: array direto ou envelope com propriedade em PascalCase/camelCase
      const data = retCursos.data;
      const listaCursos = Array.isArray(data) ? data : (Array.isArray(data?.cursos) ? data.cursos : (Array.isArray(data?.Cursos) ? data.Cursos : []));
      yield put(listaCursosEscolaSucesso(listaCursos));
    }
  } catch (error) {
    yield put(exibirMensagemErro(JSON.stringify(error)));
  }
}

function* fnBuscaEscolaEdicao({ payload }) {
  try {
    const retorno = yield call(getEscolaEdicao, payload);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      // Garante que o Local Controlado vinculado à escola esteja presente na lista exibida pelo formulário
      // mesmo quando a lista global está filtrada para "não selecionados" (IsEscola=false)
      const escola = retorno.data || {};
      const idLocal = escola.idLocalControlado || escola.IdLocalControlado;
      let localDetalhe = null;
      if (idLocal && Number(idLocal) > 0) {
        const retLocal = yield call(getLocalControladoPorId, idLocal);
        if (!retLocal.message && retLocal.data) {
          localDetalhe = retLocal.data;
        }
      }

      // Busca cursos vinculados à escola (se id existir)
      let cursosVinculados = [];
      const idEscola = Number(escola.id || escola.Id || payload || 0);
      if (idEscola > 0) {
        // Dispara busca de anos letivos para popular o combo
        yield put({ type: LISTA_ANOS_LETIVOS_POR_ESCOLA, payload: { escolaId: idEscola } });

        const retCursosEscola = yield call(getCursosDaEscola, idEscola);
        if (!retCursosEscola.message && retCursosEscola.data) {
          const dataCursos = retCursosEscola.data;
          cursosVinculados = Array.isArray(dataCursos) ? dataCursos : (Array.isArray(dataCursos?.cursos) ? dataCursos.cursos : (Array.isArray(dataCursos?.Cursos) ? dataCursos.Cursos : []));
        }
      }

      const escolaComCursos = { ...escola, cursos: cursosVinculados };
      yield put(buscaEscolaEdicaoSucesso({ escola: escolaComCursos, localControlado: localDetalhe }));
    }
  } catch (error) {
    yield put(exibirMensagemErro(JSON.stringify(error)));
  }
}

function* fnListaEscola({ payload }) {
  try {
    const { nome } = payload;
    const retorno = yield call(getListaEscola, nome);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      yield put(listaEscolaSucesso(retorno.data));
    }
  } catch (error) {
    yield put(exibirMensagemErro(JSON.stringify(error)));
  }
}

function* fnSalvaEscola({ payload }) {
  try {
    const escola = payload;
    const request = escola.id && escola.id !== 0 ? atualizaEscolaRequest : adicionaEscolaRequest;
    const retorno = yield call(request, escola);
    if (retorno.message) {
      // Trata erro de validação (HTTP 406) retornando mensagens amigáveis do backend
      const status = retorno?.response?.status;
      const data = retorno?.response?.data;
      if (status === 400) {
              let mensagemValidacao = '';
              let inconsistencias = data?.inconsistencias || data?.erros || [];
              if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
                // Ex.: [{ propriedade: 'CodigoInep', mensagem: 'Já existe ...' }]
                mensagemValidacao = inconsistencias[0].mensagem;
              } else if (data && typeof data === 'object') {
                mensagemValidacao = data.mensagem || JSON.stringify(data);
              }
              yield put(showAuthMessage(mensagemValidacao || retorno.message));
            } else {
              yield put(showAuthMessage(JSON.stringify(retorno.message)));
            }
          } else {
      const data = retorno?.data;
      if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'sucesso')) {
        if (data.sucesso === false) {
          const listaErros = Array.isArray(data.erros) ? data.erros : (Array.isArray(data.inconsistencias) ? data.inconsistencias : []);
          const mensagemValidacao = (listaErros[0] && listaErros[0].mensagem) || data.mensagem || 'Falha de validação.';
          yield put(showAuthMessage(mensagemValidacao));
          return;
        }
      }
      yield put(salvaEscolaSucesso("Cadastro salvo com sucesso!"));
      yield put({ type: LISTA_ESCOLA, payload: { nome: '' } });
    }
  } catch (error) {
    yield put(exibirMensagemErro(JSON.stringify(error)));
  }
}

function* fnAtualizaEscola({ payload }) {
  try {
    const escola = payload;
    const retorno = yield call(atualizaEscolaRequest, escola);
    if (retorno.message) {
      // Trata erro de validação (HTTP 406) retornando mensagens amigáveis do backend
      const status = retorno?.response?.status;
      const data = retorno?.response?.data;
     if (status === 400) {
             let mensagemValidacao = '';
             let inconsistencias = data?.inconsistencias || data?.erros || [];
             if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
               // Ex.: [{ propriedade: 'CodigoInep', mensagem: 'Já existe ...' }]
               mensagemValidacao = inconsistencias[0].mensagem;
             } else if (data && typeof data === 'object') {
               mensagemValidacao = data.mensagem || JSON.stringify(data);
             }
             yield put(showAuthMessage(mensagemValidacao || retorno.message));
           } else {
             yield put(showAuthMessage(JSON.stringify(retorno.message)));
           }
         } else {
      const data = retorno?.data;
      if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'sucesso')) {
        if (data.sucesso === false) {
          const listaErros = Array.isArray(data.erros) ? data.erros : (Array.isArray(data.inconsistencias) ? data.inconsistencias : []);
          const mensagemValidacao = (listaErros[0] && listaErros[0].mensagem) || data.mensagem || 'Falha de validação.';
          yield put(showAuthMessage(mensagemValidacao));
          return;
        }
      }
      yield put(salvaEscolaSucesso("Cadastro atualizado com sucesso!"));
      yield put({ type: LISTA_ESCOLA, payload: { nome: '' } });
    }
  } catch (error) {
    yield put(exibirMensagemErro(JSON.stringify(error)));
  }
}

function* fnRemoveEscola({ payload }) {
  try {
    const retorno = yield call(deleteEscolaRequest, payload);
    // Verifica se houve erro de validação (status 400 ou sucesso: false)
    const data = retorno?.response?.data || retorno?.data;
    
    // Tratamento para quando a API retorna erro 400 com inconsistências
    if (retorno?.response?.status === 400 || (data && data.sucesso === false)) {
        let mensagemErro = '';
        if (Array.isArray(data?.inconsistencias) && data.inconsistencias.length > 0) {
            mensagemErro = data.inconsistencias[0].mensagem;
        } else if (Array.isArray(data?.erros) && data.erros.length > 0) {
            mensagemErro = data.erros[0].mensagem;
        } else {
            mensagemErro = data?.mensagem || 'Erro ao excluir escola.';
        }
        yield put(showAuthMessage(mensagemErro));
        return;
    }

    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      yield put(removeEscolaSucesso("Removido com sucesso!"));
      // Após remover, força a atualização da listagem
      yield put({ type: LISTA_ESCOLA, payload: { nome: '' } });
    }
  } catch (error) {
    // Tenta extrair mensagem amigável de erro se disponível
    const msg = error?.response?.data?.mensagem || 
                (error?.response?.data?.inconsistencias?.[0]?.mensagem) || 
                JSON.stringify(error);
    yield put(exibirMensagemErro(msg));
  }
}

/*#################################################################################################################*/
/* -------------------------------------- Exportação das Sagas ----------------------------------------------------*/

export function* iniciaAcoesEscola() {
  yield takeEvery(INICIA_ESCOLA, fnIniciaEscola);
}

export function* salvaEscola() {
  // Evita múltiplos disparos de sucesso quando há duplo submit
  yield takeLatest(SALVA_ESCOLA, fnSalvaEscola);
}

export function* atualizaEscola() {
  // Evita múltiplos disparos de sucesso quando há duplo submit
  yield takeLatest(ATUALIZA_ESCOLA, fnAtualizaEscola);
}

export function* buscaEscolaEdicao() {
  yield takeEvery(BUSCA_ESCOLA_EDICAO, fnBuscaEscolaEdicao);
}

export function* removeEscola() {
  yield takeEvery(REMOVE_ESCOLA, fnRemoveEscola);
}

export function* listaEscola() {
  yield takeEvery(LISTA_ESCOLA, fnListaEscola);
}

export function* listaAnosLetivosPorEscola() {
  yield takeEvery(LISTA_ANOS_LETIVOS_POR_ESCOLA, function* ({ payload }) {
    try {
      const { escolaId } = payload;
      const retorno = yield call(getAnosLetivosPorEscola, escolaId);
      if (retorno.message) {
        yield put(showAuthMessage(JSON.stringify(retorno.message)));
      } else {
        const data = retorno.data;
        const lista = Array.isArray(data)
          ? data
          : (Array.isArray(data?.anosLetivosAtivos) ? data.anosLetivosAtivos
            : (Array.isArray(data?.anosLetivos) ? data.anosLetivos
              : (Array.isArray(data?.ativos) ? data.ativos : [])));
        // Normaliza para garantir campo title
        const listaNormalizada = lista.map(a => ({
          ...a,
          title: String(a.ano || a.title || '')
        }));
        yield put(listaAnosLetivosPorEscolaSucesso(listaNormalizada));
      }
    } catch (error) {
      yield put(exibirMensagemErro(JSON.stringify(error)));
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(iniciaAcoesEscola),
    fork(salvaEscola),
    fork(atualizaEscola),
    fork(buscaEscolaEdicao),
    fork(removeEscola),
    fork(listaEscola),
    fork(listaAnosLetivosPorEscola),
  ]);
}
